import { useMemo } from 'react';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import {
  CAPEX_BASIS_METRICS,
  CAPEX_GENETICS_BASIS_METRICS,
} from '../constants/capexMetrics';
import { getCropNames } from '../utils/farmData';
import { useDataTransforms, useFarmName } from '../utils/hooks';
import useFarmOrSectionGroupData from '../hooks/useFarmOrSectionGroupData';
import ItemsTable from '../components/tables/ItemsTable';
import { useFilterContext } from '../contexts/FilterContext';
import MetricsHeader from '../components/layout/MetricsHeader';
import Content from '../components/layout/Content';
import {
  GeneticStockBasisFragment,
  SiteInfrastructureBasisFragment,
} from '../queries';
import {
  getGeneticStockBasisData,
  getSiteInfrastructureBasisData,
} from '../utils/transforms';
import { isCropFilterValid } from '../utils/filters';

const TableHeader = styled.h3`
  margin: var(--cell-padding);
`;

const TableSection = styled.div`
  overflow: hidden;
  max-height: 60%;
  min-height: 125px;
`;

const QUERY_NAME = 'CapexBasisQuery';
const FRAGMENT_NAME = 'CapexBasisFragment';

const CapexBasisFragment = gql`
    ${GeneticStockBasisFragment}
    ${SiteInfrastructureBasisFragment}
    fragment ${FRAGMENT_NAME} on SectionsAggregate {
      ...GeneticStockBasisFragment
      ...SiteInfrastructureBasisFragment
    }
`;

const getCapexBasisData = (cropFilter) => (data) => {
  return {
    siteInfrastructure: getSiteInfrastructureBasisData(data),
    geneticStock: getGeneticStockBasisData(data, cropFilter),
  };
};

const CapexBasisView = () => {
  const { crop: cropFilter } = useFilterContext().state;

  const { msg, data } = useFarmOrSectionGroupData(
    CapexBasisFragment,
    QUERY_NAME,
    FRAGMENT_NAME,
  );
  const farmName = useFarmName();

  const getActiveCapexes = useMemo(() => getCapexBasisData(cropFilter), [
    cropFilter,
  ]);

  const [crops = [], capexBasisData = {}] = useDataTransforms(data, [
    getCropNames,
    getActiveCapexes,
  ]);

  const cropFilterValid = isCropFilterValid(data, cropFilter);
  const getCsvFilename = (title) =>
    [title, cropFilterValid ? cropFilter : 'All crops', farmName].join(' - ');

  return (
    <>
      <MetricsHeader showCropFilter showChartButton={false} crops={crops}>
        Capital Cost Basis
      </MetricsHeader>
      <Content>
        <TableSection className="mb-4">
          <ItemsTable
            data={capexBasisData.siteInfrastructure}
            msg={msg}
            cols={CAPEX_BASIS_METRICS}
            tableHeader={
              <TableHeader>Site Infrastructure Estimate</TableHeader>
            }
            csvFileName={getCsvFilename('Site infrastructure estimate')}
          />
        </TableSection>
        <TableSection className="mb-4">
          <ItemsTable
            data={capexBasisData.geneticStock}
            msg={msg}
            cols={CAPEX_GENETICS_BASIS_METRICS}
            tableHeader={<TableHeader>Genetic Stock</TableHeader>}
            csvFileName={getCsvFilename('Genetic stock')}
          />
        </TableSection>
      </Content>
    </>
  );
};

export default CapexBasisView;
